import { useState } from "react";
import { motion } from "framer-motion";
import QuoteModal from "@/components/QuoteModal";

/** Extract plain text from a ReactNode for JSON-LD */
function textOf(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (typeof node === "object" && "props" in node) {
    const { children } = (node as React.ReactElement).props || {};
    return textOf(children);
  }
  return "";
}

const faqs: { q: string; a: React.ReactNode }[] = [
  { q: "Is solid hardwood always better than engineered?", a: <>Not necessarily  and this is one of the most common misconceptions we correct. Both solid and engineered hardwood are exceptional when specified for the right environment. The key difference? Solid wood naturally expands and contracts with Michigan's dramatic humidity swings. A premium engineered floor  one built on a <strong>marine-grade core</strong> with a thick sawn face  is constructed to restrict that movement, making it far more <strong>dimensionally stable</strong>. That stability makes engineered the ideal choice for below-grade basements and a much safer option for ultra-wide planks. <a href="/product-specification" className="text-primary hover:underline font-medium">Read our Solid vs. Engineered Guide →</a></> },
  { q: "How long does hardwood need to acclimate?", a: <>Here's the truth: acclimation isn't about time  it's about measurement. The goal is for the wood to reach an <strong>Equilibrium Moisture Content</strong> that matches your home's normal living conditions. We don't guess. We use species-calibrated meters with <strong>specific gravity adjustments</strong> and insulated pin probes to test both the surface and core of every plank. Only when the precise moisture difference between the wood and your subfloor falls within strict NWFA tolerances do we begin. <a href="/environment-acclimation" className="text-primary hover:underline font-medium">Learn the science behind acclimation →</a></> },
  { q: "Do I really need my subfloor leveled?", a: <>Absolutely. Most premature flooring failures  squeaks, hollow spots, cracked locking systems  trace back to an uneven subfloor. We engineer your foundation using <strong>laser topography</strong> and premium self-leveling compounds to meet strict NWFA flatness tolerances. It's the invisible work that makes everything above it last. <a href="/subfloor-leveling" className="text-primary hover:underline font-medium">See our subfloor correction projects →</a></> },
  { q: "What's the best way to install wide plank hardwood?", a: <>Any plank 4 inches or wider demands more than nails alone. To prevent shifting and cupping, we use either a <strong>nail with glue assist</strong> method or a <strong>full trowel glue down</strong> with moisture cured urethanes. This is where experience matters  the wrong method on a wide plank can ruin a beautiful floor within a single season. <a href="/installation" className="text-primary hover:underline font-medium">Explore our installation methods →</a></> },
  { q: "Do you move furniture before installation?", a: "We coordinate furniture logistics as part of your project plan. For most homes, we ask you to clear smaller items while we handle the heavy lifting  literally." },
  { q: "Can you refinish engineered hardwood?", a: "Yes  as long as the wear layer is 2mm or greater, most engineered floors can be refinished one to three times. We always assess the wear layer thickness before recommending any sanding, because once it's gone, it's gone." },
  { q: "Do you ship flooring anywhere in Michigan?", a: "We do. Every brand we carry  Mercier, Lauzon, Viking, Cali Floors, Olde Wood Limited, and our own Custom Line  ships to any Michigan address." },
  { q: "What exactly is subfloor correction?", a: "It's the structural leveling and repair of the surface beneath your finished floor. Think of it as the foundation of your entire investment. An improperly prepared subfloor is the single leading cause of hardwood floor failures  and it's the step most contractors skip." },
];

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // FAQPage JSON-LD is baked into static HTML via generate-seo-html.mjs


  return (
    <section className="bg-neutral-50 py-14 md:py-20 lg:py-28" id="faq">
      <div className="max-w-[820px] mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-12"
        >
          <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-muted-foreground">Common Questions</p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1] mt-2">
            Frequently asked <em className="italic font-normal text-primary">questions.</em>
          </h2>
        </motion.div>

        <div>
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-b border-border"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full bg-transparent border-none py-4 md:py-5 flex justify-between items-center cursor-pointer text-left gap-3 md:gap-4 text-foreground hover:text-primary transition-colors"
              >
                <span className="text-[14px] md:text-base font-medium">{faq.q}</span>
                <span className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0 text-base md:text-lg transition-all duration-300 ${openIdx === i ? "bg-primary text-primary-foreground rotate-45" : "bg-neutral-200 text-neutral-500"}`}>
                  +
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? "max-h-80" : "max-h-0"}`}>
                <p className="pb-5 md:pb-6 text-[13px] md:text-[14.5px] font-light text-neutral-600 leading-relaxed max-w-[680px]">
                  {faq.a}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10 md:mt-12">
          <a href="tel:2483258853" className="bg-foreground text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary transition-colors text-center">
            📞 Call (248) 325-8853
          </a>
          <QuoteModal
            trigger={
              <button className="border border-border text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-background transition-colors text-center">
                Book a $50 Estimate Visit
              </button>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
